<template>
  <div class="dashboard">
    <ui-card>
      <div class="grid">
        <div class="grid-totals">
          <ui-heading no-top-margin>
            {{ identityUserName }}
            {{ $t("dashboard.performance") }}
          </ui-heading>

          <div class="date-range-select">
            <ui-date-range id="date-range" v-model="dateRange" />
          </div>

          <div class="total-idents">
            {{ $t("dashboard.total_identifications") }}:
            {{ statsCompletedIdentifications }}
          </div>

          <div class="stats">
            <div class="stat updated">
              {{ statsAggregated.updatedTotal }}<br />
              {{ $t("dashboard.updated") }}
            </div>
            <div class="stat rejected">
              {{ statsAggregated.rejectedTotal }}<br />
              {{ $t("dashboard.rejected") }}
            </div>
            <div class="stat approved">
              {{ statsAggregated.confirmedTotal }}<br />
              {{ $t("dashboard.approved") }}
            </div>
            <div class="stat avg">
              {{ slaAvg }}<br />
              {{ $t("dashboard.sla_avg") }}
            </div>
          </div>
        </div>

        <div class="grid-chart">
          <ui-overlay-loader :loading="statsLoading">
            <div id="chart" class="chart" />
            {{ error }}
          </ui-overlay-loader>
        </div>
      </div>
    </ui-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import { actions, getters } from "@/store";
import { documentDateFormat, localISODateString } from "@/utils/date";

export default {
  data() {
    const from = localISODateString(new Date());
    const to = localISODateString(new Date());
    return {
      dateRange: { from, to },
      error: null,
      loadStats: debounce(async function () {
        if (this.statsLoading) return;
        if (!this.dateRange) return;
        const { from, to } = this.dateRange;
        if (!from || !to) return;
        await this.getStats({
          from: new Date(`${from}T00:00`).toISOString(),
          to: new Date(`${to}T23:59:59.999`).toISOString()
        });
        await this.drawChart();
      }, 1000)
    };
  },
  methods: {
    ...mapActions([actions.getStats]),
    getStatCount(stat, date) {
      return this.statsAggregated[stat][date]?.count ?? 0;
    },
    async drawChart() {
      const pointStart = Date.parse(this.statsDates[0]);
      const Highcharts = await import("highcharts/highstock");
      Highcharts.setOptions({
        credits: { enabled: false },
        chart: {
          backgroundColor: "none",
          style: { fontFamily: "inherit", fontSize: "1rem" }
        }
      });
      try {
        this.chart = Highcharts.chart("chart", {
          title: {
            text: this.$t("dashboard.monthly_perf_chart_title", {
              from: documentDateFormat(new Date(this.statsDateRange?.from)),
              to: documentDateFormat(new Date(this.statsDateRange?.to))
            }),
            style: { color: "var(--text-color-base)" }
          },

          legend: {
            itemStyle: {
              color: "var(--text-color-base)",
              cursor: "default",
              fontSize: "0.75rem",
              textShadow: "none"
            },
            itemHoverStyle: {
              color: "var(--text-color-base)",
              textShadow: "0 0 1px currentColor"
            },
            itemHiddenStyle: { color: "var(--text-color-disabled)" }
          },

          yAxis: {
            title: {
              text: this.$t("dashboard.chart_identification_amount"),
              style: { color: "var(--text-color-base)" }
            },
            labels: {
              style: { color: "var(--text-color-base)" }
            }
          },

          plotOptions: {
            series: {
              borderWidth: 0,
              pointStart,
              pointInterval: 24 * 3600 * 1000,
              lineWidth: 5,
              marker: {
                enabled: false
              }
            }
          },

          xAxis: {
            type: "datetime",
            labels: { style: { color: "var(--text-color-base)" } }
          },
          series: this.series
        });
      } catch (error) {
        this.error = error.message;
      }
    }
  },
  computed: {
    ...mapGetters([
      getters.identityUserName,
      getters.statsDateRange,
      getters.statsLoading,
      getters.statsLogsAll,
      getters.statsAggregated,
      getters.statsAvgDecisionTime,
      getters.statsDates
    ]),
    statsCompletedIdentifications() {
      const { rejectedTotal, confirmedTotal } = this.statsAggregated;
      return rejectedTotal + confirmedTotal;
    },
    slaAvg() {
      const time = this.statsAvgDecisionTime;
      if (!time) return "-:--";
      const minutes = Math.floor(time / 1000 / 60);
      const seconds = (Math.floor(time / 1000) - minutes * 60)
        .toString()
        .padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    series() {
      const data = this.statsDates.reduce(
        (data, date, index) => {
          data.updated.push(this.getStatCount("updatedDaily", date));
          data.updatedAccumulated.push(
            (data.updatedAccumulated[index - 1] ?? 0) + data.updated[index]
          );
          data.confirmed.push(this.getStatCount("confirmedDaily", date));
          data.confirmedAccumulated.push(
            (data.confirmedAccumulated[index - 1] ?? 0) + data.confirmed[index]
          );
          data.rejected.push(this.getStatCount("rejectedDaily", date));
          data.rejectedAccumulated.push(
            (data.rejectedAccumulated[index - 1] ?? 0) + data.rejected[index]
          );
          return data;
        },
        {
          updated: [],
          updatedAccumulated: [],
          confirmed: [],
          confirmedAccumulated: [],
          rejected: [],
          rejectedAccumulated: []
        }
      );
      return [
        {
          name: this.$t("dashboard.updatedTotal"),
          data: data.updatedAccumulated,
          color: "var(--text-warning)"
        },
        {
          type: "column",
          name: this.$t("dashboard.updatedDaily"),
          data: data.updated,
          color: "var(--text-warning)"
        },
        {
          name: this.$t("dashboard.rejectedTotal"),
          data: data.rejectedAccumulated,
          color: "var(--text-danger)"
        },
        {
          type: "column",
          name: this.$t("dashboard.rejectedDaily"),
          data: data.rejected,
          color: "var(--text-danger)"
        },
        {
          name: this.$t("dashboard.approvedTotal"),
          data: data.confirmedAccumulated,
          color: "var(--text-success)"
        },
        {
          type: "column",
          name: this.$t("dashboard.approvedDaily"),
          data: data.confirmed,
          color: "var(--text-success)"
        }
      ];
    }
  },
  mounted() {
    this.loadStats();
  },
  beforeDestroy() {
    this.loadStats.cancel();
  },
  watch: {
    dateRange() {
      this.loadStats();
    }
  }
};
</script>

<style scoped lang="scss">
.dashboard {
  padding: 1rem;
}
.grid {
  @media all and (min-width: 70rem) {
    display: grid;
    grid-template-columns: 30rem 1fr;
    column-gap: 1rem;
  }
}
.chart {
  width: 100%;
  height: 30rem;
}
.total-idents {
  margin: 2rem 0;
  font-size: 1.2rem;
  text-align: center;
}
.stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 1rem;
  column-gap: 1rem;
  margin-bottom: 2rem;
}
.stat {
  padding: 1rem;
  font-weight: bold;
  text-align: center;
  border-radius: 1rem;

  &.rejected {
    color: var(--button-danger-text-color);
    background-color: var(--button-danger-background);
  }
  &.approved {
    color: var(--button-success-text-color);
    background-color: var(--button-success-background);
  }
  &.updated {
    color: var(--button-warning-text-color);
    background-color: var(--button-warning-background);
  }
  &.avg {
    color: var(--button-info-text-color);
    background-color: var(--button-info-background);
  }
}
.date-range-select {
  padding: 1rem 0;
}
</style>
